import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import type { ChartOptions } from './m-compare-stacked-bar-chart.component.types';
import { NgClass, DecimalPipe } from '@angular/common';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'm-compare-stacked-bar-chart',
  templateUrl: './m-compare-stacked-bar-chart.component.html',
  styleUrl: './m-compare-stacked-bar-chart.component.scss',
  imports: [NgClass, DecimalPipe]
})
export class MCompareStackedBarChartComponent implements OnInit, OnChanges {
  private readonly MIN_TOP_SECTION_HEIGHT = 10;
  private readonly MAX_BOTTOM_SECTION_HEIGHT = 90;
  private readonly PERCENTAGE_MULTIPLIER = 100;
  private readonly HALF_DIVISOR = 2;
  private readonly BOTTOM_POSITION_OFFSET = 8;
  private readonly TOP_POSITION_OFFSET = 3;

  @Input() options!: ChartOptions;

  leftLabel = '';
  rightTopLabel = '';
  rightBottomLabel = '';
  topValue = 0;
  bottomValue = 0;
  difference = 0;

  rightTopSectionHeight = 0;
  rightBottomSectionHeight = 0;
  rightTopLabelPosition = 0;
  rightBottomLabelPosition = 0;

  ngOnInit(): void {
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && changes['options'].currentValue) {
      this.updateChart();
    }
  }

  private updateChart(): void {
    this.calculateDifference();
    this.setLabels();
    this.calculateHeightsAndPositions();
  }

  private calculateDifference(): void {
    this.difference =
      this.options.data.referenceValue - this.options.data.compareValue;
  }

  private setLabels(): void {
    if (this.difference > 0) {
      this.leftLabel = this.options.labels.referenceValueLabel;
      this.rightBottomLabel = this.options.labels.compareValueLabel;
    } else {
      this.leftLabel = this.options.labels.compareValueLabel;
      this.rightBottomLabel = this.options.labels.referenceValueLabel;
    }
    this.rightTopLabel = this.options.labels.differenceValueLabel;
  }

  private calculateHeightsAndPositions(): void {
    this.setValuesBasedOnDifference();
    this.calculateSectionHeights();
    this.applyMinHeightThreshold();
    this.calculateLabelPositions();
  }

  private setValuesBasedOnDifference(): void {
    const isPositive = this.difference > 0;
    this.topValue = Math.abs(this.difference);
    this.bottomValue = isPositive
      ? this.options.data.compareValue
      : this.options.data.referenceValue;
  }

  private calculateSectionHeights(): void {
    const baseValue = this.bottomValue + this.topValue;
    this.rightTopSectionHeight =
      (this.topValue / baseValue) * this.PERCENTAGE_MULTIPLIER;
    this.rightBottomSectionHeight =
      (this.bottomValue / baseValue) * this.PERCENTAGE_MULTIPLIER;
  }

  private applyMinHeightThreshold(): void {
    if (this.rightTopSectionHeight < this.MIN_TOP_SECTION_HEIGHT) {
      this.rightTopSectionHeight = this.MIN_TOP_SECTION_HEIGHT;
      this.rightBottomSectionHeight = this.MAX_BOTTOM_SECTION_HEIGHT;
    }
  }

  private calculateLabelPositions(): void {
    this.rightTopLabelPosition =
      this.rightTopSectionHeight / this.HALF_DIVISOR - this.TOP_POSITION_OFFSET;
    this.rightBottomLabelPosition =
      this.rightTopSectionHeight +
      (this.rightBottomSectionHeight / this.HALF_DIVISOR -
        this.BOTTOM_POSITION_OFFSET);
  }
}
